import { type KeyboardEvent } from 'react'
import { msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTheme, alpha } from '@mui/material/styles'
import BalanceIcon from '@mui/icons-material/Balance'
import GavelIcon from '@mui/icons-material/Gavel'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'

type SensitiveDataStepProps = {
  checked: boolean
  onChange: (checked: boolean) => void
}

const sensitiveDataMessages = {
  intro: msg`Doctor, we remind you of your duty to inform the patient:`,
  carePurpose: msg`Their data is necessary for care and medical follow-up (primary purpose).`,
  discrimination: msg`Collection is not likely to give rise to discrimination.`,
  categoriesTitle: msg`Sensitive data`,
  confirmation: msg`I commit to informing the patient`,
}

const sensitiveCategoryMessages = [
  msg`Racial/ethnic origin`,
  msg`Political opinions`,
  msg`Religious/philosophical beliefs`,
  msg`Trade union membership`,
  msg`Genetic data`,
  msg`Biometric data`,
  msg`Sex life/orientation`,
]

export function SensitiveDataStep({ checked, onChange }: SensitiveDataStepProps) {
  const { i18n } = useLingui()
  const theme = useTheme()

  const sensitiveCategories = sensitiveCategoryMessages.map((category) => i18n._(category))
  const toggleChecked = () => onChange(!checked)
  const handleToggleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== ' ' && event.key !== 'Enter') return

    event.preventDefault()
    toggleChecked()
  }

  return (
    <Stack spacing={3} sx={{ pt: 1 }}>
      {/* Intro */}
      <Typography variant="body1" fontWeight={500} sx={{ lineHeight: 1.7, color: theme.palette.text.primary }}>
        {i18n._(sensitiveDataMessages.intro)}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <Box sx={{ p: 2.5, bgcolor: 'grey.50', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <HealthAndSafetyIcon sx={{ color: theme.palette.primary.main, fontSize: 26 }} />
          <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ lineHeight: 1.6 }}>
            {i18n._(sensitiveDataMessages.carePurpose)}
          </Typography>
        </Box>
        <Box sx={{ p: 2.5, bgcolor: 'grey.50', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <BalanceIcon sx={{ color: theme.palette.primary.main, fontSize: 26 }} />
          <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ lineHeight: 1.6 }}>
            {i18n._(sensitiveDataMessages.discrimination)}
          </Typography>
        </Box>
      </Box>

      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <GavelIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {i18n._(sensitiveDataMessages.categoriesTitle)}
          </Typography>
        </Stack>
        <Box sx={{ p: 2, bgcolor: alpha(theme.palette.grey[200], 0.3), borderRadius: '12px' }}>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {sensitiveCategories.map((category) => (
              <Chip
                key={category}
                label={category}
                size="small"
                variant="outlined"
                sx={{
                  bgcolor: 'white',
                  borderColor: alpha(theme.palette.divider, 0.2),
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  height: 28,
                }}
              />
            ))}
          </Stack>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          bgcolor: checked ? alpha(theme.palette.primary.main, 0.06) : 'grey.100',
          borderRadius: '12px',
          border: checked ? `1.5px solid ${alpha(theme.palette.primary.main, 0.3)}` : '1.5px solid transparent',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': { bgcolor: checked ? alpha(theme.palette.primary.main, 0.08) : 'grey.200' },
        }}
        onClick={toggleChecked}
        onKeyDown={handleToggleKeyDown}
        role="checkbox"
        tabIndex={0}
        aria-checked={checked}
      >
        <Checkbox
          checked={checked}
          inputProps={{ readOnly: true }}
          sx={{ p: 0, color: theme.palette.grey[400], '&.Mui-checked': { color: theme.palette.primary.main } }}
        />
        <Typography variant="body2" fontWeight={600} color={checked ? 'primary' : 'text.primary'}>
          {i18n._(sensitiveDataMessages.confirmation)}
        </Typography>
      </Box>
    </Stack>
  )
}
